import type { FormResponseValue } from "@tellescope/types-models";
import { parseDateStringAnswer } from "./parse-date-string-answer";
import { assembleTimeAnswer } from "./parse-time-answer";

/**
 * Field-type-agnostic cleanup applied to a form response at the validation and submission
 * boundaries (i.e. what validation evaluates and what is sent to the backend), NOT to the value
 * held in local state while typing. Each field type that needs cleanup gets a case here; types
 * with no cleanup pass through unchanged. Add new per-type normalization here rather than at the
 * individual call sites.
 *
 * Current behavior:
 * - string / stringLong: trim leading/trailing whitespace (a whitespace-only value collapses to
 *   empty). This intentionally differs from the legacy engine, which stored/submitted verbatim.
 * - dateString: rewrite to `MM-DD-YYYY`, expanding a 2-digit year, so a typed or pasted
 *   `MM-DD-YY` is stored in the canonical shape. An unparseable value passes through for
 *   validation to reject.
 * - Time: assemble the bare 24-hour `HH:mm` held in local state into `HH:MM AmPm Zone`, stamping
 *   the current local zone. `convertTimeResponseToLocalValue` is the inverse, applied when an
 *   existing response is read back into local state.
 */
export const normalizeResponseForSubmission = (response: FormResponseValue): FormResponseValue => {
  switch (response.answer.type) {
    case "string":
    case "stringLong":
      if (typeof response.answer.value === "string") {
        return { ...response, answer: { ...response.answer, value: response.answer.value.trim() } };
      }
      return response;
    case "dateString": {
      if (typeof response.answer.value !== "string") return response;
      const parts = parseDateStringAnswer(response.answer.value);
      if (!parts) return response;
      return { ...response, answer: { ...response.answer, value: parts.normalized } };
    }
    case "Time": {
      if (typeof response.answer.value !== "string") return response;
      const assembled = assembleTimeAnswer(response.answer.value);
      if (!assembled) return response;
      return { ...response, answer: { ...response.answer, value: assembled } };
    }
    default:
      return response;
  }
};
